export interface SettlementLevelInput {
  name: string;
  startingChips: number;
  initialCounts: Record<string, number>;
  raCounts: Record<string, number>;
  activityFee1: number;
  prizeContribution: number;
  participationPoints: number;
  rankPoints: number[];
}

export const LEVEL_JSON_MAX_BYTES = 64 * 1024;
export const DEFAULT_RANK_POINTS = [3, 2, 1];
const keys = [
  'name',
  'startingChips',
  'initialCounts',
  'raCounts',
  'activityFee1',
  'prizeContribution',
  'participationPoints',
  'rankPoints',
];
const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
export const nonnegativeInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;

export function chipTotal(counts: Record<string, number>): number {
  return Object.entries(counts).reduce((sum, [face, count]) => sum + Number(face) * count, 0);
}

function validCounts(value: unknown): value is Record<string, number> {
  if (!record(value)) return false;
  const entries = Object.entries(value);
  return (
    entries.length > 0 &&
    entries.length <= 12 &&
    entries.every(([key, count]) => {
      const face = Number(key);
      return (
        Number.isSafeInteger(face) &&
        face > 0 &&
        String(face) === key &&
        nonnegativeInteger(count) &&
        Number.isSafeInteger(face * count)
      );
    }) &&
    Number.isSafeInteger(chipTotal(value as Record<string, number>))
  );
}

export function validLevel(value: unknown): value is SettlementLevelInput {
  if (!record(value) || Object.keys(value).some((key) => !keys.includes(key))) return false;
  return (
    typeof value.name === 'string' &&
    value.name.trim().length > 0 &&
    value.name.trim().length <= 128 &&
    validCounts(value.initialCounts) &&
    validCounts(value.raCounts) &&
    nonnegativeInteger(value.startingChips) &&
    value.startingChips > 0 &&
    chipTotal(value.initialCounts) === value.startingChips &&
    nonnegativeInteger(value.activityFee1) &&
    nonnegativeInteger(value.prizeContribution) &&
    value.prizeContribution > value.activityFee1 &&
    nonnegativeInteger(value.participationPoints) &&
    Array.isArray(value.rankPoints) &&
    value.rankPoints.length <= 50 &&
    value.rankPoints.every(nonnegativeInteger)
  );
}

export function parseSettlementLevelJson(text: string): SettlementLevelInput {
  if (new TextEncoder().encode(text).byteLength > LEVEL_JSON_MAX_BYTES)
    throw Error('Invalid level JSON');
  let value: unknown = JSON.parse(text);
  if (record(value) && ('version' in value || 'type' in value || 'level' in value)) {
    if (
      value.version !== 1 ||
      value.type !== 'pokerroom-timed-settlement-level' ||
      Object.keys(value).some((key) => !['version', 'type', 'level'].includes(key))
    ) {
      throw Error('Invalid level JSON');
    }
    value = value.level;
  }
  if (!record(value)) throw Error('Invalid level JSON');
  const normalized = {
    ...value,
    rankPoints: value.rankPoints === undefined ? [...DEFAULT_RANK_POINTS] : value.rankPoints,
  };
  if (!validLevel(normalized)) throw Error('Invalid level JSON');
  return { ...normalized, name: normalized.name.trim() };
}

export function exportSettlementLevelJson(level: SettlementLevelInput): string {
  if (!validLevel(level)) throw Error('Invalid level');
  return JSON.stringify({ version: 1, type: 'pokerroom-timed-settlement-level', level }, null, 2);
}
